// src/Pages/BlitzPay/extras/BBCSplitApprovalDialog.jsx
//
// Shown inside BlitzPay.jsx when an enterprise user clicks a pending
// BBC / Blitz Split invoice from the PendingItemsModal or the activity feed.
//
// This is the DIALOG variant (modal) — as opposed to BBCSplitInvoiceDesign
// which is the full invoice page. They share the same action logic but have
// different layout contexts.
//
// Data flow
// ─────────
// 1. Receives a sparse `invoice` prop (enriched item from BlitzPay.jsx)
// 2. On open, calls client.invoices.fetch(id) → GET /invoice/:id
//    which (after the backend patch) returns creator_payments + bbc_summary
// 3. Falls back to prop data gracefully if the fetch fails
// 4. Approve:  client.invoices.unifiedAction({ invoiceId, action: 'approve' })
// 5. Decline:  client.invoices.unifiedAction({ invoiceId, action: 'decline' })
// 6. Convert:  client.bbc.convertToBlitzPay(bbc_id) → then approve

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Avatar, Button, Chip, Divider,
  IconButton, Stack, Collapse, Paper, CircularProgress,
  Alert, Tooltip, LinearProgress, Card, CardContent,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Bolt as BoltIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  FlashOn as FlashOnIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import client from '../../../../API';

// ── Design tokens ─────────────────────────────────────────────────── //
const Y      = '#FFD600';
const Y_DARK = '#F5C800';
const Y_INK  = '#1A1200';
const Y_TINT = alpha('#FFD600', 0.07);
const Y_BDR  = alpha('#FFD600', 0.25);
const Y_DEEP = '#8C6D00';
const SLATE  = '#1e293b';

const usd = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);

const termLabel = (days) => {
  if (days == null || days === '') return 'Standard';
  if (Number(days) === 0) return 'Due on receipt';
  return `Net ${days}`;
};

// ── Single creator row inside the roster ───────────────────────────── //
const CreatorRow = ({ payment }) => {
  const name =
    payment.recipient_name ||
    payment.creator_id     ||
    payment.recipient_email ||
    'Contractor';

  const amt = parseFloat(payment.amount || 0);
  const pre = payment.preapproved || payment.blitzpay_preapproved || payment.blitzpay;

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        py: 1.1, px: 1.5, borderRadius: 2,
        bgcolor: pre ? Y_TINT : 'transparent',
        border: '1px solid',
        borderColor: pre ? Y_BDR : 'transparent',
        transition: 'background 0.15s',
      }}
    >
      <Avatar
        sx={{
          width: 28, height: 28,
          fontSize: '0.7rem', fontWeight: 800,
          bgcolor: pre ? Y : alpha('#000', 0.07),
          color: pre ? Y_INK : 'text.secondary',
          flexShrink: 0,
        }}
      >
        {name.charAt(0).toUpperCase()}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap sx={{ lineHeight: 1.2 }}>
          {name}
        </Typography>
        {payment.recipient_email && payment.recipient_email !== name && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ fontSize: '0.62rem', display: 'block' }}
            noWrap
          >
            {payment.recipient_email}
          </Typography>
        )}
      </Box>

      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
        {pre && (
          <Tooltip title="Pre-accepted — instant payout elected by the sender">
            <Chip
              icon={<BoltIcon sx={{ fontSize: '10px !important' }} />}
              label="⚡ Instant"
              size="small"
              sx={{
                height: 18, fontSize: '0.6rem', fontWeight: 800,
                bgcolor: Y, color: Y_INK,
                '& .MuiChip-icon': { color: Y_INK, ml: '4px' },
              }}
            />
          </Tooltip>
        )}
        <Typography
          variant="body2"
          fontWeight={800}
          sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 60, textAlign: 'right' }}
        >
          {usd(amt)}
        </Typography>
      </Stack>
    </Box>
  );
};

// ── Meta info row ──────────────────────────────────────────────────── //
const MetaRow = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
    <Box sx={{ color: Y_DEEP, mt: '1px', flexShrink: 0 }}>{icon}</Box>
    <Typography
      variant="body2"
      sx={{ color: 'text.secondary', minWidth: 105, flexShrink: 0, fontSize: '0.8rem' }}
    >
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem', color: SLATE }}>
      {value || '—'}
    </Typography>
  </Box>
);

// ── Main dialog ────────────────────────────────────────────────────── //
const BBCSplitApprovalDialog = ({
  open,
  onClose,
  invoice: invoiceProp,
  onSuccess,
}) => {
  const [invoice, setInvoice]       = useState(null);
  const [payments, setPayments]     = useState([]);
  const [bbcMeta, setBbcMeta]       = useState(null);
  const [loading, setLoading]       = useState(false);

  const [balance, setBalance]       = useState(null);
  const [balLoading, setBalLoading] = useState(false);

  const [rosterOpen, setRosterOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMode, setActionMode] = useState(null);
  const [error, setError]           = useState(null);
  const [confirmed, setConfirmed]   = useState(null);

  // ── Fetch full invoice data on open ─────────────────────────────── //
  useEffect(() => {
    if (!open || !invoiceProp?.id) return;

    setInvoice(null);
    setPayments([]);
    setBbcMeta(null);
    setError(null);
    setConfirmed(null);
    setRosterOpen(false);
    setLoading(true);

    client.invoices.fetch(invoiceProp.id)
      .then((data) => {
        const full = data?.invoice || data;
        setInvoice(full);

        // creator_payments (BBC-specific) preferred over payouts
        const roster =
          data?.creator_payments?.length  ? data.creator_payments  :
          data?.payouts?.length           ? data.payouts            :
          [];
        setPayments(roster);

        if (data?.bbc_summary) setBbcMeta(data.bbc_summary);
      })
      .catch(() => setInvoice(invoiceProp))
      .finally(() => setLoading(false));

  }, [open, invoiceProp?.id]);

  // ── Fetch caller's Blitz balance ────────────────────────────────── //
  useEffect(() => {
    if (!open) return;
    setBalLoading(true);
    client.balanceLedger.fetchMyBalance()
      .then((d) => setBalance(parseFloat(d?.balance ?? 0)))
      .catch(() => setBalance(null))
      .finally(() => setBalLoading(false));
  }, [open]);

  if (!invoiceProp) return null;

  // ── Derived values ───────────────────────────────────────────────── //
  const inv          = invoice || invoiceProp;
  const total        = parseFloat(inv.amount_due || inv.amount || 0);
  const senderName   = inv.client_name || invoiceProp.client_name || invoiceProp.name || 'Agency';
  const senderEmail  = inv.client_email || invoiceProp.client_email || '';
  const poNumber     = inv.po_number || invoiceProp.po_number || '—';
  const paymentTerm  = inv.payment_term ?? invoiceProp.payment_term;
  const status       = (inv.status || invoiceProp.status || inv.invoice_status || '').toLowerCase();
  const alreadyDone  = ['approved','paid','funded','cancelled'].includes(status);

  const remaining    = payments.filter((p) => p.status === 'promised');
  const preCount     = payments.filter((p) => p.preapproved || p.blitzpay_preapproved || p.blitzpay).length;
  const recipHint    = payments.length > 0
    ? payments.length
    : invoiceProp?.custom_details?.recipient_count ?? null;

  // Conversion fee (uses bbcMeta if available, falls back to 5%)
  const feeRate       = parseFloat(bbcMeta?.instant_fee_rate || invoiceProp?.custom_details?.instant_fee_rate || 5);
  const grossRemaining = remaining.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const fee           = Math.round(grossRemaining * feeRate) / 100;
  const canAfford     = balance !== null && balance >= fee;
  const hasRemaining  = remaining.length > 0;
  const bbcId         = bbcMeta?.bbc_id || invoiceProp?.custom_details?.bbc_id;

  // ── Actions ──────────────────────────────────────────────────────── //
  const doAction = async (mode) => {
    setActionLoading(true);
    setActionMode(mode);
    setError(null);
    try {
      if (mode === 'convert' && bbcId) {
        await client.bbc.convertToBlitzPay(bbcId);
      }
      const res = await client.invoices.unifiedAction({
        invoiceId: inv.id || invoiceProp.id,
        action: 'approve',
      });
      setConfirmed(mode === 'convert' ? 'approved_instant' : 'approved');
      setTimeout(() => {
        onSuccess?.(
          res?.message ||
          (mode === 'convert'
            ? 'Split approved — all contractors credited instantly ⚡'
            : 'Split approved — contractors credited at funding.')
        );
        onClose?.();
      }, 1500);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
        (mode === 'convert'
          ? 'Conversion failed — check your balance and try again.'
          : 'Approval failed. Please try again.')
      );
    } finally {
      setActionLoading(false);
      setActionMode(null);
    }
  };

  const doDecline = async () => {
    setActionLoading(true);
    setActionMode('decline');
    setError(null);
    try {
      const res = await client.invoices.unifiedAction({
        invoiceId: inv.id || invoiceProp.id,
        action: 'decline',
      });
      setConfirmed('declined');
      setTimeout(() => {
        onSuccess?.(res?.message || 'Invoice declined.');
        onClose?.();
      }, 1500);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not decline invoice.');
    } finally {
      setActionLoading(false);
      setActionMode(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────── //
  return (
    <Dialog
      open={open}
      onClose={!actionLoading ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3.5,
          overflow: 'hidden',
          boxShadow: `0 24px 64px ${alpha('#000', 0.18)}`,
        },
      }}
    >
      {/* Yellow top bar */}
      <Box
        sx={{
          height: 5,
          background: `linear-gradient(90deg, ${Y} 0%, ${alpha(Y, 0.4)} 100%)`,
        }}
      />

      {/* ── Header ──────────────────────────────────────────────────── */}
      <DialogTitle sx={{ pb: 0, pt: 2.5, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Avatar
            sx={{ width: 40, height: 40, bgcolor: Y, color: Y_INK, flexShrink: 0, mt: 0.25 }}
          >
            <BoltIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" gap={0.5}>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                Blitz Split
              </Typography>
              <Chip
                label="Pending approval"
                size="small"
                sx={{
                  height: 20, fontSize: '0.62rem', fontWeight: 700,
                  bgcolor: Y_TINT, color: Y_DEEP, border: `1px solid ${Y_BDR}`,
                }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
              From <strong>{senderName}</strong>
              {senderEmail ? ` · ${senderEmail}` : ''}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={actionLoading}
            sx={{ mt: -0.5, mr: -0.5, flexShrink: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 0 }}>
        {/* Fetch progress bar */}
        {loading && (
          <LinearProgress
            sx={{
              mb: 2, borderRadius: 1,
              bgcolor: alpha(Y, 0.15),
              '& .MuiLinearProgress-bar': { bgcolor: Y },
            }}
          />
        )}

        {/* ── Success / declined state ─────────────────────────────── */}
        {confirmed && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1.5 }}>
            {confirmed === 'approved_instant' && (
              <>
                <Avatar sx={{ width: 56, height: 56, bgcolor: Y, color: Y_INK }}>
                  <BoltIcon sx={{ fontSize: 30 }} />
                </Avatar>
                <Typography variant="h6" fontWeight={800} textAlign="center">
                  Split approved + converted ⚡
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  All contractors have been credited instantly to their Blitz balance.
                </Typography>
              </>
            )}
            {confirmed === 'approved' && (
              <>
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'success.main', color: 'white' }}>
                  <CheckCircleIcon sx={{ fontSize: 30 }} />
                </Avatar>
                <Typography variant="h6" fontWeight={800}>Split approved</Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Contractors will be credited when the payment lands.
                  {preCount > 0 && ` ${preCount} pre-accepted ⚡ row${preCount !== 1 ? 's' : ''} convert instantly.`}
                </Typography>
              </>
            )}
            {confirmed === 'declined' && (
              <>
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'error.light', color: 'error.dark' }}>
                  <CancelIcon sx={{ fontSize: 30 }} />
                </Avatar>
                <Typography variant="h6" fontWeight={800}>Invoice declined</Typography>
                <Typography variant="body2" color="text.secondary">
                  {senderName} has been notified.
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* ── Main content ─────────────────────────────────────────── */}
        {!confirmed && (
          <>
            {/* Summary card */}
            <Paper
              variant="outlined"
              sx={{ p: 2.5, borderRadius: 2.5, borderColor: Y_BDR, bgcolor: Y_TINT, mb: 2 }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700, color: Y_DEEP,
                    textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.62rem',
                  }}
                >
                  Invoice total
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 900,
                    fontVariantNumeric: 'tabular-nums',
                    color: SLATE,
                    lineHeight: 1.1,
                    mt: 0.25,
                  }}
                >
                  {usd(total)}
                </Typography>
              </Box>

              <Divider sx={{ borderColor: Y_BDR, mb: 2 }} />

              <Stack spacing={1.25}>
                <MetaRow
                  icon={<BusinessIcon sx={{ fontSize: 15 }} />}
                  label="From"
                  value={senderName}
                />
                <MetaRow
                  icon={<ReceiptIcon sx={{ fontSize: 15 }} />}
                  label="PO / Reference"
                  value={poNumber}
                />
                <MetaRow
                  icon={<CalendarIcon sx={{ fontSize: 15 }} />}
                  label="Payment terms"
                  value={termLabel(paymentTerm)}
                />
                <MetaRow
                  icon={<PersonIcon sx={{ fontSize: 15 }} />}
                  label="Contractors"
                  value={
                    loading && payments.length === 0
                      ? 'Loading…'
                      : payments.length > 0
                        ? `${payments.length} total${preCount > 0 ? ` · ${preCount} ⚡ pre-accepted` : ''}`
                        : recipHint
                          ? `${recipHint} contractor${recipHint !== 1 ? 's' : ''}`
                          : 'Roster attached'
                  }
                />
              </Stack>
            </Paper>

            {/* Context sentence */}
            <Paper
              elevation={0}
              sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', mb: 2 }}
            >
              <Typography variant="body2" sx={{ lineHeight: 1.65, color: 'text.secondary' }}>
                Approving commits you to pay <strong style={{ color: SLATE }}>{usd(total)}</strong> on{' '}
                <strong style={{ color: SLATE }}>{termLabel(paymentTerm)}</strong> terms to{' '}
                <strong style={{ color: SLATE }}>{senderName}</strong>.
                {payments.length > 0
                  ? ` Each contractor is notified now and credited when the wire lands.`
                  : ''}
                {preCount > 0
                  ? ` ${preCount} row${preCount !== 1 ? 's' : ''} are pre-elected ⚡ — their early-payout fee (${feeRate}%) comes from their slice, not yours.`
                  : ''}
              </Typography>
            </Paper>

            {/* ── Roster ───────────────────────────────────────────── */}
            {payments.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box
                  onClick={() => setRosterOpen(!rosterOpen)}
                  sx={{
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer', py: 0.75, px: 0.5,
                    borderRadius: 1.5, userSelect: 'none',
                    '&:hover': { bgcolor: 'action.hover' },
                    transition: 'background 0.15s',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '0.08em', fontSize: '0.62rem', color: 'text.secondary',
                    }}
                  >
                    Contractor roster
                    {preCount > 0 && !rosterOpen ? ` · ${preCount} ⚡` : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: Y_DEEP, fontWeight: 700, fontSize: '0.68rem' }}>
                      {rosterOpen ? 'Collapse' : `Show all ${payments.length}`}
                    </Typography>
                    {rosterOpen
                      ? <ExpandLessIcon sx={{ fontSize: 16, color: Y_DEEP }} />
                      : <ExpandMoreIcon sx={{ fontSize: 16, color: Y_DEEP }} />}
                  </Box>
                </Box>

                {/* Proportion bar */}
                {!rosterOpen && (
                  <Box sx={{ mt: 0.5, height: 5, borderRadius: 3, overflow: 'hidden', display: 'flex', gap: '2px' }}>
                    {payments.map((p, i) => (
                      <Box
                        key={p.id || i}
                        sx={{
                          flex: parseFloat(p.amount || 1),
                          bgcolor: (p.preapproved || p.blitzpay_preapproved || p.blitzpay)
                            ? Y
                            : alpha('#000', 0.1),
                          borderRadius: 3,
                        }}
                      />
                    ))}
                  </Box>
                )}

                <Collapse in={rosterOpen}>
                  <Stack spacing={0.6} sx={{ mt: 1 }}>
                    {payments.map((p, i) => (
                      <CreatorRow key={p.id || i} payment={p} />
                    ))}

                    {/* Subtotals */}
                    <Box
                      sx={{
                        pt: 1.25, mt: 0.5,
                        borderTop: '1px solid', borderColor: 'divider',
                        px: 0.5,
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                          Contractor total
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={800}
                          sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {usd(payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0))}
                        </Typography>
                      </Stack>
                      {bbcMeta?.sme_margin && parseFloat(bbcMeta.sme_margin) > 0 && (
                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.4 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Agency fee
                          </Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {usd(bbcMeta.sme_margin)}
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                </Collapse>
              </Box>
            )}

            {/* Loading hint while roster fetches */}
            {loading && payments.length === 0 && (
              <Box sx={{ mb: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={13} sx={{ color: Y_DEEP }} />
                <Typography variant="caption" color="text.secondary">
                  Loading contractor roster…
                </Typography>
              </Box>
            )}

            {/* ── BlitzPay Conversion offer ──────────────────────── */}
            {hasRemaining && !alreadyDone && bbcId && (
              <Card
                variant="outlined"
                sx={{
                  mb: 2, borderRadius: 2.5,
                  borderColor: Y_BDR,
                  bgcolor: Y_TINT,
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb: 1.5 }}>
                    <FlashOnIcon sx={{ color: Y_DEEP, fontSize: 18, mt: '1px', flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={800} sx={{ color: SLATE, lineHeight: 1.2 }}>
                        Upgrade to instant BlitzPay
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pay a {feeRate}% fee ({usd(fee)}) from your Blitz balance.
                        All {remaining.length} remaining contractor{remaining.length !== 1 ? 's' : ''}{' '}
                        credited right now — no waiting for the wire.
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 1 }}>
                      <Typography variant="body2" fontWeight={900} sx={{ color: Y_DEEP }}>
                        {usd(fee)}
                      </Typography>
                      {balLoading
                        ? <CircularProgress size={10} sx={{ color: Y_DEEP, mt: 0.3 }} />
                        : (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block', fontWeight: 700, fontSize: '0.62rem',
                              color: canAfford ? 'success.main' : 'error.main',
                            }}
                          >
                            {canAfford
                              ? `Balance: ${usd(balance)} ✓`
                              : `Need ${usd(fee - (balance || 0))} more`}
                          </Typography>
                        )
                      }
                    </Box>
                  </Stack>

                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    disabled={!canAfford || actionLoading || balLoading}
                    onClick={() => doAction('convert')}
                    startIcon={
                      actionLoading && actionMode === 'convert'
                        ? <CircularProgress size={13} sx={{ color: Y_INK }} />
                        : <BoltIcon sx={{ fontSize: '16px !important' }} />
                    }
                    sx={{
                      bgcolor: Y, color: Y_INK, fontWeight: 800,
                      borderRadius: 2, textTransform: 'none', py: 1,
                      fontSize: '0.82rem',
                      boxShadow: `0 3px 12px ${alpha(Y, 0.35)}`,
                      '&:hover': { bgcolor: Y_DARK },
                      '&.Mui-disabled': { bgcolor: alpha(Y, 0.25), color: alpha(Y_INK, 0.35) },
                    }}
                  >
                    {actionLoading && actionMode === 'convert'
                      ? 'Converting…'
                      : `Convert + Approve — pay ${usd(fee)}`}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Error */}
            {error && (
              <Alert
                severity="error"
                icon={<WarningIcon />}
                onClose={() => setError(null)}
                sx={{ mb: 2, borderRadius: 2 }}
              >
                {error}
              </Alert>
            )}

            {/* Already processed */}
            {alreadyDone && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                This invoice has already been {status}.
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      {/* ── Dialog actions ────────────────────────────────────────────── */}
      {!confirmed && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
          <Button
            variant="outlined"
            color="error"
            size="large"
            disabled={actionLoading || alreadyDone}
            onClick={doDecline}
            startIcon={
              actionLoading && actionMode === 'decline'
                ? <CircularProgress size={14} color="inherit" />
                : <CancelIcon />
            }
            sx={{ flex: 1, borderRadius: 2.5, fontWeight: 700, textTransform: 'none', py: 1.4 }}
          >
            Decline
          </Button>

          <Button
            variant="outlined"
            size="large"
            disabled={actionLoading || alreadyDone}
            onClick={() => doAction('approve')}
            startIcon={
              actionLoading && actionMode === 'approve'
                ? <CircularProgress size={14} color="inherit" />
                : <CheckCircleIcon />
            }
            sx={{
              flex: 2, borderRadius: 2.5, fontWeight: 800,
              fontSize: '0.9rem', textTransform: 'none', py: 1.4,
              borderColor: Y_BDR, color: Y_DEEP,
              '&:hover': { borderColor: Y, bgcolor: Y_TINT },
            }}
          >
            {actionLoading && actionMode === 'approve'
              ? 'Approving…'
              : hasRemaining ? 'Approve — pay at funding' : 'Approve'}
          </Button>
        </DialogActions>
      )}

      {confirmed && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            sx={{ borderRadius: 2.5, fontWeight: 700, textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default BBCSplitApprovalDialog;